import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '@/apiConfig';
import { isStaticCatalogOnly, fetchStaticCatalogProducts } from '@/utils/staticCatalog';
import { getSubmenuLabel } from '@/constants/categorySubmenus';
import { getFoodSubcategorySvgSrc } from '@/constants/categoryFoodSubmenuSvg';
import { getSubcategoryThumbSrc } from '@/constants/categorySubmenuThumbs';
import { useLanguage } from '@/contexts/LanguageContext';
import { getCategoryTitle } from './CategoryGrid';
import MegaMenuPanel from './mega-menu/MegaMenuPanel';
import { getProductDisplayName } from '@/utils/catalogDisplay';

export default function CategoryMegaMenuRightColumn({
  hoveredCat,
  hoveredSubs,
  lang,
  selectCategoryAndClose,
  onNavigate,
  closePanel,
}) {
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(false);
  const { tl } = useLanguage();

  useEffect(() => {
    if (!hoveredCat?.key) {
      setSamples([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const run = async () => {
      try {
        if (isStaticCatalogOnly()) {
          const all = await fetchStaticCatalogProducts();
          if (cancelled) return;
          const key = String(hoveredCat.key || "").trim().toLowerCase();
          const match = all
            .filter((p) => (p.category || "").trim().toLowerCase() === key)
            .slice(0, 4);
          setSamples(match);
        } else {
          const res = await axios.get(`${API}/products`, {
            params: { category: hoveredCat.key, limit: 4, skip: 0 },
          });
          if (cancelled) return;
          setSamples((res.data?.products || []).slice(0, 4));
        }
      } catch {
        if (!cancelled) setSamples([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [hoveredCat?.key]);

  if (!hoveredCat || hoveredSubs.length === 0) return null;

  const title = getCategoryTitle(hoveredCat, lang);
  const browseSub = tl('Browse subcategories', 'Parcourir les sous-categories', 'Explorar subcategorias', 'تصفح الفئات الفرعية');
  const featured = tl(
    'Featured in this category',
    'Selection dans cette categorie',
    'Destacados en esta categoria',
    'مميز في هذه الفئة'
  );
  const emptyFeatured = tl(
    'No featured products in this category yet. Browse the full catalog.',
    'Aucun produit exemple charge ; essayez la page produits.',
    'No se cargaron productos de muestra; pruebe la pagina de productos.',
    'لم يتم تحميل عينات منتجات؛ جرب صفحة المنتجات.'
  );

  const subcategories = hoveredSubs.map((sub, idx) => ({
    listKey: `${sub.key}-${idx}`,
    key: sub.key,
    label: getSubmenuLabel(sub, lang, tl, hoveredCat.key),
    navSearch: sub.navSearch,
    gridSpacer: Boolean(sub.gridSpacer),
  }));

  const viewAllLabel = tl('View all', 'Voir tout', 'Ver todo', 'عرض الكل');

  return (
    <div key={hoveredCat.id} className="animate-in fade-in-0 duration-200">
      <MegaMenuPanel
        gridTitle={title}
        gridSubtitle={browseSub}
        viewAllLabel={viewAllLabel}
        onViewAll={() => selectCategoryAndClose(hoveredCat.key)}
        subcategoryGridColumns={hoveredCat.id === 'food' ? 3 : 2}
        subcategories={subcategories}
        onSubcategorySelect={(cat) => {
          if (cat.gridSpacer) return;
          selectCategoryAndClose(hoveredCat.key, cat.navSearch ? '' : cat.key, cat.navSearch || '');
        }}
        products={samples}
        loading={loading}
        lang={lang}
        featuredLabel={featured}
        emptyFeaturedMessage={emptyFeatured}
        productLineHint={title}
        onProductClick={(p) => {
          onNavigate?.('product', p.id);
          closePanel?.();
        }}
        actionLabel={tl('Add to RFQ Basket', 'Ajouter au panier RFQ', 'Agregar a la cesta RFQ', 'اضافة الى سلة RFQ', 'RFQ sepetine ekle')}
        getProductName={(p, lng) => getProductDisplayName(p, lng)}
        resolveSubcategoryIconSrc={(key) => {
          const food = getFoodSubcategorySvgSrc(key);
          if (food) return food;
          return getSubcategoryThumbSrc(key);
        }}
      />
    </div>
  );
}
